import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { AlertTriangle, ArrowLeft, ArrowLeftRight, Calculator, CheckCircle2, Copy, ExternalLink, Plus, Settings, Trash, Trash2, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input, Textarea } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NlDateInput } from '@/components/ui/nl-date-input';
import { Select } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import {
	api,
	documentPdfUrl,
	type ExactCreditor,
	type ExactLedger,
	type ExactVatCode,
	type InkoopProposal,
	type InkoopProposalLine,
} from '@/lib/api';
import { EXACT_STATUS_LABELS, PAYMENT_STATUS_DOT, PAYMENT_STATUS_LABELS } from '@/lib/labels';
import {
	applyInkoopCreditor,
	approveInkoop,
	createExactCreditor,
	deleteDocument,
	patchInkoopProposal,
	rejectInkoop,
	reopenInkoop,
	requeueInkoop,
} from '@/lib/mutations';
import { formatDateISO } from '@/lib/utils';
export const Route = createFileRoute('/inkoopfacturen/$id')({
	component: InkoopApprovalPage,
});

function InkoopApprovalPage() {
	const { id } = Route.useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const meQuery = useQuery({ queryKey: ['me'], queryFn: () => api.me() });
	const docQuery = useQuery({ queryKey: ['inkoopfactuur', id], queryFn: () => api.inkoopfactuur(id) });

	const doc = docQuery.data?.document as
		| (Record<string, unknown> & { entity_code: string; entity_name: string; mime_type?: string; original_filename?: string; exact_status?: string })
		| undefined;
	const proposal = docQuery.data?.proposal ?? null;
	const entityCode = doc?.entity_code ?? '';

	const creditorsQuery = useQuery({
		queryKey: ['exact-creditors', entityCode],
		queryFn: () => api.exactCreditors(entityCode),
		enabled: Boolean(entityCode),
	});
	const ledgersQuery = useQuery({
		queryKey: ['exact-ledgers', entityCode],
		queryFn: () => api.exactLedgers(entityCode),
		enabled: Boolean(entityCode),
	});
	const vatQuery = useQuery({
		queryKey: ['exact-vat-codes', entityCode],
		queryFn: () => api.exactVatCodes(entityCode),
		enabled: Boolean(entityCode),
	});

	// Lokale draft van het voorstel — schrijf bij elke wijziging in state, sla
	// expliciet op via "Opslaan" of bij goedkeuren.
	const [draft, setDraft] = useState<InkoopProposal | null>(null);
	useEffect(() => {
		if (proposal) setDraft(structuredClone(proposal));
	}, [proposal]);

	const [error, setError] = useState<string | null>(null);
	const [showCreateCreditor, setShowCreateCreditor] = useState(false);

	// Eager fetch van de werklijst voor "ga naar volgende"-flow. Gevraagd binnen
	// dezelfde entiteits-scope als de huidige factuur zodat de volgende ook
	// binnen je toegang valt. Backend filtert al op user.entity_access.
	const pendingListQuery = useQuery({
		queryKey: ['inkoopfacturen', 'pending_for_next', doc?.entity_code],
		queryFn: () =>
			api.inkoopfacturen({
				entity_code: doc?.entity_code,
				exact_status: 'pending_review',
				limit: 50,
			}),
		enabled: Boolean(doc?.entity_code),
	});

	const goToNextPending = () => {
		const items = pendingListQuery.data?.items ?? [];
		const next = items.find((item) => item.id !== id);
		if (next) {
			navigate({ to: '/inkoopfacturen/$id', params: { id: next.id } });
		} else {
			navigate({ to: '/inkoopfacturen' });
		}
	};

	const patchMutation = useMutation({
		mutationFn: (next: InkoopProposal) => patchInkoopProposal(id, next),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ['inkoopfactuur', id] }),
		onError: (err: unknown) => {
			const body = err && typeof err === 'object' && 'body' in err ? (err as { body: unknown }).body : err;
			setError(typeof body === 'object' ? JSON.stringify(body) : String(body));
		},
	});
	const approveMutation = useMutation({
		mutationFn: async () => {
			if (draft) await patchInkoopProposal(id, draft);
			return approveInkoop(id);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['inkoopfactuur', id] });
			queryClient.invalidateQueries({ queryKey: ['inkoopfacturen'] });
			goToNextPending();
		},
		onError: (err: unknown) => {
			const body = err && typeof err === 'object' && 'body' in err ? (err as { body: unknown }).body : err;
			setError(typeof body === 'object' ? JSON.stringify(body) : String(body));
		},
	});
	const rejectMutation = useMutation({
		mutationFn: () => rejectInkoop(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['inkoopfactuur', id] });
			queryClient.invalidateQueries({ queryKey: ['inkoopfacturen'] });
			goToNextPending();
		},
	});
	const reopenMutation = useMutation({
		mutationFn: () => reopenInkoop(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['inkoopfactuur', id] });
			queryClient.invalidateQueries({ queryKey: ['inkoopfacturen'] });
		},
		onError: (err: unknown) => {
			const body = err && typeof err === 'object' && 'body' in err ? (err as { body: unknown }).body : err;
			setError(typeof body === 'object' ? JSON.stringify(body) : String(body));
		},
	});
	// Goedgekeurd maar nooit in Exact aangekomen (retries uitgeput, DLQ):
	// opnieuw op de export-queue zetten zonder het voorstel opnieuw te keuren.
	const requeueMutation = useMutation({
		mutationFn: () => requeueInkoop(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['inkoopfactuur', id] });
			queryClient.invalidateQueries({ queryKey: ['inkoopfacturen'] });
		},
		onError: (err: unknown) => {
			const body = err && typeof err === 'object' && 'body' in err ? (err as { body: unknown }).body : err;
			setError(typeof body === 'object' ? JSON.stringify(body) : String(body));
		},
	});
	const deleteMutation = useMutation({
		mutationFn: () => deleteDocument(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['inkoopfacturen'] });
			queryClient.invalidateQueries({ queryKey: ['documents'] });
			navigate({ to: '/inkoopfacturen' });
		},
		onError: (err: unknown) => {
			const body = err && typeof err === 'object' && 'body' in err ? (err as { body: unknown }).body : err;
			setError(typeof body === 'object' ? JSON.stringify(body) : String(body));
		},
	});

	if (docQuery.isLoading || meQuery.isLoading) {
		return <div className="p-12 text-center text-sm text-muted-foreground">Laden…</div>;
	}
	if (!doc) {
		return (
			<div className="mx-auto max-w-md p-12 text-center text-sm">
				<AlertTriangle className="mx-auto mb-2 h-6 w-6 text-destructive" />
				Inkoopfactuur niet gevonden of geen toegang.
			</div>
		);
	}
	const status = doc.exact_status ?? 'pending_review';
	const isLocked = status !== 'pending_review';
	const canApprove = meQuery.data?.can_approve_exact === true;

	return (
		<div className="space-y-4">
			<div className="flex flex-wrap items-start gap-3">
				<Button variant="ghost" size="sm" asChild>
					<Link to="/inkoopfacturen">
						<ArrowLeft className="mr-1.5 h-4 w-4" />
						Terug
					</Link>
				</Button>
				<div className="flex-1 min-w-0">
					<h1 className="truncate text-xl font-semibold">
						{(doc.counterparty_name as string) ?? 'Inkoopfactuur'}
					</h1>
					<p className="mt-0.5 text-xs text-muted-foreground">
						{doc.entity_name} · {(doc.original_filename as string) ?? doc.id} ·{' '}
						{(doc.amount_total as string) ?? '—'} {(doc.currency as string) ?? ''}
					</p>
				</div>
				<div className="flex items-center gap-2">
					<Badge variant="muted">{EXACT_STATUS_LABELS[status as keyof typeof EXACT_STATUS_LABELS] ?? status}</Badge>
					{(status === 'not_applicable' || status === 'failed') && canApprove && (
						<Button
							variant="outline"
							size="sm"
							onClick={() => reopenMutation.mutate()}
							disabled={reopenMutation.isPending}
						>
							<ArrowLeft className="mr-1.5 h-4 w-4 rotate-180" />
							Heropenen
						</Button>
					)}
					{status === 'approved' && !doc.exact_entry_id && canApprove && (
						<Button
							variant="outline"
							size="sm"
							title="De factuur is goedgekeurd maar nooit in Exact aangekomen. Zet hem opnieuw op de export-queue."
							onClick={() => requeueMutation.mutate()}
							disabled={requeueMutation.isPending}
						>
							<ArrowLeftRight className="mr-1.5 h-4 w-4" />
							{requeueMutation.isPending ? 'Bezig…' : 'Opnieuw naar Exact'}
						</Button>
					)}
					{!isLocked && draft && JSON.stringify(draft) !== JSON.stringify(proposal) && (
						<Button
							variant="outline"
							size="sm"
							onClick={() => draft && patchMutation.mutate(draft)}
							disabled={patchMutation.isPending}
						>
							{patchMutation.isPending ? 'Opslaan…' : 'Opslaan'}
						</Button>
					)}
					{!isLocked && canApprove && (
						<>
							<Button
								variant="outline"
								size="sm"
								className="text-destructive hover:bg-destructive/10 hover:text-destructive"
								onClick={() => {
									if (window.confirm('Deze factuur niet inboeken in Exact?')) rejectMutation.mutate();
								}}
								disabled={rejectMutation.isPending}
							>
								<X className="mr-1.5 h-4 w-4" />
								Afwijzen
							</Button>
							<Button
								size="sm"
								onClick={() => approveMutation.mutate()}
								disabled={approveMutation.isPending}
							>
								<CheckCircle2 className="mr-1.5 h-4 w-4" />
								{approveMutation.isPending ? 'Bezig…' : 'Goedkeuren & boeken'}
							</Button>
						</>
					)}
					{/* Verwijderen — alleen toegestaan zolang de factuur NIET succesvol naar Exact is gestuurd. */}
					{status !== 'sent' && status !== 'already_present' && (
						<Button
							variant="outline"
							size="sm"
							className="text-destructive hover:bg-destructive/10 hover:text-destructive"
							onClick={() => {
								if (
									window.confirm(
										'Deze inkoopfactuur volledig verwijderen?\n\nPDF + metadata + audit-trail worden permanent gewist. Dit kan niet ongedaan gemaakt worden.',
									)
								)
									deleteMutation.mutate();
							}}
							disabled={deleteMutation.isPending}
						>
							<Trash className={deleteMutation.isPending ? 'mr-1.5 h-4 w-4 animate-pulse' : 'mr-1.5 h-4 w-4'} />
							Verwijderen
						</Button>
					)}
				</div>
			</div>

			{error && (
				<div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
					{error}
				</div>
			)}
			{(doc.exact_error_reason as string | undefined) && (
				<div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
					Vorige boekpoging: {doc.exact_error_reason as string}
				</div>
			)}
			{(status === 'sent' || status === 'already_present') && (doc.exact_entry_id as string | undefined) && (
				<ExactBookedBanner
					entryId={doc.exact_entry_id as string}
					alreadyPresent={status === 'already_present'}
					paymentStatus={(doc.payment_status as 'paid' | 'partial' | 'open' | null) ?? null}
					paymentDate={(doc.payment_date as string | null) ?? null}
					paymentSyncedAt={(doc.payment_status_synced_at as string | null) ?? null}
				/>
			)}

			<div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
				<div className="space-y-4 xl:order-1">
					{!draft ? (
						<Card>
							<CardContent className="p-6 text-sm text-muted-foreground">
								Geen voorstel beschikbaar.
							</CardContent>
						</Card>
					) : (
						<>
							<HeaderCard
								docId={id}
								draft={draft}
								setDraft={setDraft}
								creditors={creditorsQuery.data?.items ?? []}
								ledgers={ledgersQuery.data?.items ?? []}
								documentIban={(doc.counterparty_iban as string | null) ?? null}
								disabled={isLocked}
								entityCode={entityCode}
							/>
							<LinesCard
								draft={draft}
								setDraft={setDraft}
								ledgers={ledgersQuery.data?.items ?? []}
								vatCodes={vatQuery.data?.items ?? []}
								invoiceTotal={(doc.amount_total as string | null) ?? null}
								disabled={isLocked}
								entityIsVatExempt={Boolean(doc.entity_is_vat_exempt)}
							/>
							<CommentCard draft={draft} setDraft={setDraft} disabled={isLocked} />
						</>
					)}
				</div>

				<Card className="xl:order-2">
					<CardHeader className="pb-2">
						<CardTitle className="text-sm font-medium text-muted-foreground">Document</CardTitle>
					</CardHeader>
					<CardContent className="p-0">
						{doc.mime_type?.toString().startsWith('image/') ? (
							<div className="flex h-[88vh] items-center justify-center overflow-auto rounded-b-xl border-t bg-muted/20 p-4">
								<img
									src={documentPdfUrl(id)}
									alt={doc.original_filename as string}
									className="max-h-full max-w-full object-contain"
								/>
							</div>
						) : (
							<iframe src={documentPdfUrl(id)} title="document" className="h-[88vh] w-full rounded-b-xl border-t" />
						)}
					</CardContent>
				</Card>
			</div>

			{showCreateCreditor && doc && (
				<CreateCreditorDialog
					entityCode={entityCode}
					prefill={{
						name: (doc.counterparty_name as string) ?? '',
						address_line1: (doc.counterparty_address as string)?.split('\n')[0] ?? '',
						postcode: parsePostcode((doc.counterparty_address as string) ?? ''),
						city: parseCity((doc.counterparty_address as string) ?? ''),
						chamber_of_commerce: (doc.counterparty_kvk as string) ?? '',
						vat_number: (doc.counterparty_vat as string) ?? '',
						email: (doc.counterparty_email as string) ?? '',
						phone: (doc.counterparty_phone as string) ?? '',
					}}
					onClose={() => setShowCreateCreditor(false)}
					onCreated={(creditor) => {
						setShowCreateCreditor(false);
						setDraft((d) =>
							d
								? {
										...d,
										header: { ...d.header, exact_account_id: creditor.exact_id },
									}
								: d,
						);
						queryClient.invalidateQueries({ queryKey: ['exact-creditors', entityCode] });
					}}
				/>
			)}
		</div>
	);
}

// Exact returneert BTW-percentages als decimaal (0.21 = 21%). Voor display
// (en voor de proposal_json.vat_percent) zetten we ze om naar het normale
// integer-percentage. Waarden ≥ 1 gaan ongewijzigd door (defensief, voor
// het geval Exact ooit een andere format gebruikt).
function formatVatPercent(raw: string): string {
	const n = Number(raw);
	if (!Number.isFinite(n)) return raw;
	if (n > 0 && n < 1) {
		// Round naar 2 decimalen om afrondingsfouten op zeldzame tarieven
		// (bv 0.0571 / 5.71%) leesbaar te tonen.
		const pct = n * 100;
		return Number.isInteger(pct) ? pct.toString() : pct.toFixed(2).replace(/\.?0+$/, '');
	}
	return n.toString();
}

// Bereken vat_amount voor een regel uit excl-bedrag + percentage. Gebruikt voor
// auto-recalc bij wijziging van amount_excl_vat of vat_code. Voor reverse-
// charged codes geeft dit de fictieve BTW die we ook in proposal_json bewaren.
function recalcVatAmount(
	excl: string | null,
	vatPercent: string | null,
): string | null {
	if (!excl || !vatPercent) return null;
	const e = Number(excl);
	const p = Number(vatPercent);
	if (!Number.isFinite(e) || !Number.isFinite(p)) return null;
	return ((e * p) / 100).toFixed(2);
}

function parsePostcode(address: string): string {
	const m = /\b(\d{4}\s*[A-Z]{2})\b/.exec(address);
	return m?.[1] ?? '';
}
function parseCity(address: string): string {
	const m = /\b\d{4}\s*[A-Z]{2}\s+(.+)/.exec(address);
	return m?.[1]?.split('\n')[0]?.trim() ?? '';
}

// ---------------- Header ----------------

function HeaderCard({
	docId,
	draft,
	setDraft,
	creditors,
	ledgers,
	documentIban,
	disabled,
	entityCode,
}: {
	docId: string;
	draft: InkoopProposal;
	setDraft: React.Dispatch<React.SetStateAction<InkoopProposal | null>>;
	creditors: ExactCreditor[];
	ledgers: ExactLedger[];
	documentIban: string | null;
	disabled: boolean;
	entityCode: string;
}) {
	const matched = useMemo(
		() => creditors.find((c) => c.exact_id === draft.header.exact_account_id),
		[creditors, draft.header.exact_account_id],
	);
	const [showDefaults, setShowDefaults] = useState(false);

	const updateHeader = (patch: Partial<typeof draft.header>) =>
		setDraft((d) => (d ? { ...d, header: { ...d.header, ...patch } } : d));

	const periode = draft.header.factuurdatum ? draft.header.factuurdatum.slice(0, 7).replace('-', '/') : '';

	// IBAN-mismatch: factuur kan een andere IBAN tonen dan in Exact bij de
	// crediteur staat — voor een nieuwe of gewijzigde betaalrekening kan dat
	// een legitieme reden zijn, maar voor reguliere facturen is het signal
	// fraudegevoelig (factuur-IBAN-fraude). Banner waarschuwt; gebruiker checkt.
	const normalizeIban = (s: string | null | undefined): string | null =>
		s ? s.replace(/\s+/g, '').toUpperCase() : null;
	const docIban = normalizeIban(documentIban);
	const creditorIban = normalizeIban(matched?.iban ?? null);
	const ibanMismatch =
		docIban && creditorIban && docIban !== creditorIban
			? { documentIban: docIban, creditorIban }
			: null;

	return (
		<Card>
			<CardContent className="space-y-3 p-4 text-sm">
				<div>
					<div className="flex items-end justify-between gap-2">
						<Label className="text-xs">Crediteur *</Label>
						{matched && !disabled && (
							<button
								type="button"
								onClick={() => setShowDefaults(true)}
								className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
								title="Defaults van deze crediteur instellen"
							>
								<Settings className="h-3 w-3" />
								Crediteur-instellingen
							</button>
						)}
					</div>
					<Select
						value={draft.header.exact_account_id ?? ''}
						onChange={(e) => {
							const newId = e.target.value || null;
							// Optimistisch de header bijwerken, dan server-side de crediteur-
							// defaults (grootboek/BTW/betalingsreferentie) op de regels toepassen
							// en het bijgewerkte voorstel overnemen.
							const next: InkoopProposal = { ...draft, header: { ...draft.header, exact_account_id: newId } };
							setDraft(next);
							if (newId) {
								applyInkoopCreditor(docId, next)
									.then((r) => setDraft(r.proposal as InkoopProposal))
									.catch(() => { /* houd de optimistische header-update aan */ });
							}
						}}
						disabled={disabled}
						className="h-8"
					>
						<option value="">— kies of zoek —</option>
						{creditors.map((c) => (
							<option key={c.exact_id} value={c.exact_id}>
								{c.name}
								{c.code ? ` (${c.code})` : ''}
							</option>
						))}
					</Select>
					{matched && (
						<div className="mt-1 space-y-0.5 text-[11px] text-muted-foreground">
							{(matched.address_line1 || matched.postcode || matched.city) && (
								<div>
									{matched.address_line1}
									{matched.postcode || matched.city
										? `, ${matched.postcode ?? ''} ${matched.city ?? ''}`.trim()
										: ''}
								</div>
							)}
							<div className="flex flex-wrap gap-x-3">
								{matched.our_account && <span>Klantnr: <span className="font-mono">{matched.our_account}</span></span>}
								{matched.iban && <span>IBAN: <span className="font-mono">{matched.iban}</span></span>}
							</div>
						</div>
					)}
					{ibanMismatch && (
						<div className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
							<strong>⚠ IBAN-controle:</strong> de IBAN op de factuur (
							<span className="font-mono">{ibanMismatch.documentIban}</span>) wijkt af van de IBAN bij de
							crediteur in Exact (<span className="font-mono">{ibanMismatch.creditorIban}</span>).
							Controleer voor goedkeuring of dit een legitieme rekeningwijziging is.
						</div>
					)}
				</div>
				{showDefaults && matched && (
					<CreditorDefaultsDialog
						entityCode={entityCode}
						creditor={matched}
						onClose={() => setShowDefaults(false)}
						onSaved={() => setShowDefaults(false)}
					/>
				)}

				<div className="grid grid-cols-2 gap-x-4 gap-y-3">
					{/* Linkerkolom */}
					<div>
						<Label className="text-xs">Factuurnummer *</Label>
						<Input
							value={draft.header.factuurnummer ?? ''}
							onChange={(e) => updateHeader({ factuurnummer: e.target.value || null })}
							disabled={disabled}
							className="h-8"
						/>
					</div>
					<div>
						<Label className="text-xs">Betalingsreferentie</Label>
						<Input
							value={draft.header.payment_reference ?? ''}
							onChange={(e) => updateHeader({ payment_reference: e.target.value || null })}
							disabled={disabled}
							className="h-8"
							placeholder={draft.header.factuurnummer ?? '(zelfde als factuurnummer)'}
						/>
					</div>
					<div>
						<Label className="text-xs">Ordernummer</Label>
						<Input
							value={draft.header.order_reference ?? ''}
							onChange={(e) => updateHeader({ order_reference: e.target.value || null })}
							disabled={disabled}
							className="h-8"
							placeholder="bv. 123456 (uit factuur)"
						/>
					</div>
					<div />{/* spacer voor tweekoloms-grid */}
					<div>
						<Label className="text-xs">Factuurdatum *</Label>
						<NlDateInput
							value={draft.header.factuurdatum ?? ''}
							onChange={(iso) => updateHeader({ factuurdatum: iso || null })}
							disabled={disabled}
							className="h-8"
						/>
					</div>
					<div>
						<Label className="text-xs">Periode</Label>
						<Input value={periode} readOnly className="h-8 bg-muted/40 font-mono" />
					</div>
					<div>
						<Label className="text-xs">Vervaldatum</Label>
						<NlDateInput
							value={draft.header.vervaldatum ?? ''}
							onChange={(iso) => updateHeader({ vervaldatum: iso || null })}
							disabled={disabled}
							className="h-8"
						/>
					</div>
					<div>
						<Label className="text-xs">Valuta</Label>
						<Input value="EUR" readOnly className="h-8 bg-muted/40 font-mono" />
					</div>
				</div>

				<div className="flex flex-wrap items-center gap-4 pt-1">
					<label className="flex items-center gap-2 text-xs">
						<input
							type="checkbox"
							checked={draft.header.is_credit_note}
							onChange={(e) => updateHeader({ is_credit_note: e.target.checked })}
							disabled={disabled}
						/>
						<ArrowLeftRight className="h-3 w-3" />
						Creditfactuur
					</label>
					<label className="flex items-center gap-2 text-xs">
						<input
							type="checkbox"
							checked={draft.header.line_split_mode === 'single'}
							onChange={(e) => {
								const next = e.target.checked;
								setDraft((d) => {
									if (!d) return d;
									const nextHeader = {
										...d.header,
										line_split_mode: next ? ('single' as const) : ('multi' as const),
									};
									if (!next) return { ...d, header: nextHeader };
									// Bij aanzetten: non-shipping mergen naar 1 regel.
									const ns = d.lines.filter((l) => !l.is_shipping);
									const sh = d.lines.filter((l) => l.is_shipping);
									if (ns.length <= 1) return { ...d, header: nextHeader };
									const ledgerCode = ns[0]?.ledger_account ?? null;
									const ledger = ledgerCode ? ledgers.find((l) => l.code === ledgerCode) : null;
									const creditor = creditors.find(
										(c) => c.exact_id === d.header.exact_account_id,
									);
									const factuur = d.header.factuurnummer ?? '';
									const parts = [ledger?.description, creditor?.name, factuur].filter(
										Boolean,
									) as string[];
									const merged: InkoopProposalLine = {
										description: (parts.join(' ') || 'Inkoopfactuur').slice(0, 60),
										ledger_account: ledgerCode,
										vat_code: ns[0]?.vat_code ?? null,
										vat_percent: ns[0]?.vat_percent ?? null,
										amount_excl_vat: ns
											.reduce((s, l) => s + Number(l.amount_excl_vat ?? '0'), 0)
											.toFixed(2),
										vat_amount: ns
											.reduce((s, l) => s + Number(l.vat_amount ?? '0'), 0)
											.toFixed(2),
										is_shipping: false,
									};
									return { ...d, header: nextHeader, lines: [merged, ...sh] };
								});
							}}
							disabled={disabled}
						/>
						Boekingsregels samenvoegen
					</label>
				</div>
			</CardContent>
		</Card>
	);
}

// ---------------- Lines grid ----------------

function LinesCard({
	draft,
	setDraft,
	ledgers,
	vatCodes,
	invoiceTotal,
	disabled,
	entityIsVatExempt,
}: {
	draft: InkoopProposal;
	setDraft: React.Dispatch<React.SetStateAction<InkoopProposal | null>>;
	ledgers: ExactLedger[];
	vatCodes: ExactVatCode[];
	invoiceTotal: string | null;
	disabled: boolean;
	entityIsVatExempt: boolean;
}) {
	const updateLine = (idx: number, patch: Partial<InkoopProposalLine>) =>
		setDraft((d) =>
			d
				? {
						...d,
						lines: d.lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)),
					}
				: d,
		);
	const addLine = () =>
		setDraft((d) =>
			d
				? {
						...d,
						lines: [
							...d.lines,
							{
								description: '',
								ledger_account: null,
								vat_code: null,
								vat_percent: null,
								amount_excl_vat: null,
								vat_amount: null,
								is_shipping: false,
							},
						],
					}
				: d,
		);
	const removeLine = (idx: number) =>
		setDraft((d) => (d ? { ...d, lines: d.lines.filter((_, i) => i !== idx) } : d));

	const isCredit = draft.header.is_credit_note;

	// Reverse-charged regels (verlegd / EU / extra-EU) hebben fictieve BTW —
	// die hoort NIET bij het regel-totaal of bij het factuurtotaal. Voor die
	// regels: total = excl. Bij normale codes: total = excl + btw.
	const isReverseCharged = (vatCode: string | null) => {
		if (!vatCode) return false;
		const v = vatCodes.find((x) => x.code === vatCode);
		return Boolean(v?.is_reverse_charged);
	};
	const lineTotal = (l: InkoopProposalLine): number => {
		const excl = l.amount_excl_vat ? Number(l.amount_excl_vat) : 0;
		const vat = l.vat_amount ? Number(l.vat_amount) : 0;
		return isReverseCharged(l.vat_code) ? excl : excl + vat;
	};

	const totalExcl = draft.lines.reduce(
		(sum, l) => sum + (l.amount_excl_vat ? Number(l.amount_excl_vat) : 0),
		0,
	);
	const totalVatActual = draft.lines.reduce(
		(sum, l) => (isReverseCharged(l.vat_code) ? sum : sum + (l.vat_amount ? Number(l.vat_amount) : 0)),
		0,
	);
	const totalVatFictief = draft.lines.reduce(
		(sum, l) => (isReverseCharged(l.vat_code) ? sum + (l.vat_amount ? Number(l.vat_amount) : 0) : sum),
		0,
	);
	const totalIncl = draft.lines.reduce((sum, l) => sum + lineTotal(l), 0);
	const invoiceTotalNum = invoiceTotal ? Number(invoiceTotal) : null;
	const invoiceTotalSigned =
		invoiceTotalNum !== null && draft.header.is_credit_note ? -invoiceTotalNum : invoiceTotalNum;
	const totalDiff =
		invoiceTotalSigned !== null ? Number((totalIncl - invoiceTotalSigned).toFixed(2)) : null;
	const totalMatches = totalDiff !== null && Math.abs(totalDiff) < 0.01;

	const isSingle = draft.header.line_split_mode === 'single';

	return (
		<Card>
			<CardHeader className="pb-3 flex flex-row items-center justify-between">
				<CardTitle className="text-sm font-medium">Boekingsregels</CardTitle>
				{!disabled && !isSingle && (
					<Button variant="outline" size="sm" onClick={addLine} className="h-7 px-3">
						<Plus className="mr-1.5 h-3 w-3" />
						Regel
					</Button>
				)}
			</CardHeader>
			<CardContent className="space-y-2 text-sm">
				{isCredit && (
					<div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
						<strong>Creditfactuur</strong> — alle bedragen worden negatief geboekt in Exact.
					</div>
				)}
				{entityIsVatExempt && (
					<div className="rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
						<strong>Niet BTW-plichtig</strong> — bedragen worden inclusief BTW geboekt op het grootboek; er
						gaat geen BTW-code of BTW-bedrag naar Exact.
					</div>
				)}
				{isSingle && (
					<div className="rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
						<strong>Samenvoegen actief</strong> — de artikelregels zijn samengevoegd tot één boekingsregel
						(verzendkosten blijven los). Je kunt omschrijving en bedragen vrij aanpassen. Schakel uit
						om weer met meerdere regels te werken (oorspronkelijke uitsplitsing wordt niet hersteld).
					</div>
				)}
				<div className="grid grid-cols-12 gap-2 px-2 pt-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
					<div className={entityIsVatExempt ? 'col-span-5' : 'col-span-3'}>Omschrijving</div>
					<div className={entityIsVatExempt ? 'col-span-3' : 'col-span-2'}>Grootboek</div>
					{!entityIsVatExempt && <div className="col-span-2">BTW-code</div>}
					<div className="col-span-2 text-right">
						{entityIsVatExempt ? 'Bedrag (incl.)' : 'Bedrag excl. BTW'}
					</div>
					{!entityIsVatExempt && <div className="col-span-2 text-right">BTW</div>}
					<div className="col-span-1" />
				</div>
				{draft.lines.map((line, idx) => (
					<LineRow
						key={idx}
						line={line}
						ledgers={ledgers}
						vatCodes={vatCodes}
						disabled={disabled}
						isCredit={draft.header.is_credit_note}
						entityIsVatExempt={entityIsVatExempt}
						onChange={(patch) => updateLine(idx, patch)}
						onRemove={draft.lines.length > 1 ? () => removeLine(idx) : null}
					/>
				))}
				<Separator />
				<div className="flex flex-wrap items-center justify-end gap-6 text-xs text-muted-foreground">
					{!entityIsVatExempt && (
						<>
							<div>
								Totaal excl. BTW: <span className="font-mono text-foreground">{totalExcl.toFixed(2)}</span>
							</div>
							<div>
								BTW: <span className="font-mono text-foreground">{totalVatActual.toFixed(2)}</span>
							</div>
							{totalVatFictief > 0 && (
								<div title="BTW verlegd / EU / extra-EU — verschijnt in de aangifte maar niet in het factuurtotaal.">
									BTW (verlegd):{' '}
									<span className="font-mono text-foreground">{totalVatFictief.toFixed(2)}</span>
								</div>
							)}
						</>
					)}
					<div>
						{entityIsVatExempt ? 'Totaal: ' : 'Totaal incl.: '}
						<span className="font-mono font-semibold text-foreground">{totalIncl.toFixed(2)}</span>
					</div>
					{invoiceTotalSigned !== null && (
						<div className={totalMatches ? 'text-emerald-700' : 'text-destructive'}>
							Factuurtotaal: <span className="font-mono">{invoiceTotalSigned.toFixed(2)}</span>
							{!totalMatches && totalDiff !== null && (
								<span className="ml-2 font-mono">(verschil {totalDiff > 0 ? '+' : ''}{totalDiff.toFixed(2)})</span>
							)}
						</div>
					)}
				</div>
				{invoiceTotalSigned !== null && !totalMatches && (
					<div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
						<strong>Som van regels wijkt af van factuurtotaal.</strong> Controleer de bedragen voordat je goedkeurt.
					</div>
				)}
			</CardContent>
		</Card>
	);
}

function LineRow({
	line,
	ledgers,
	vatCodes,
	disabled,
	isCredit,
	entityIsVatExempt,
	onChange,
	onRemove,
}: {
	line: InkoopProposalLine;
	ledgers: ExactLedger[];
	vatCodes: ExactVatCode[];
	disabled: boolean;
	isCredit: boolean;
	entityIsVatExempt: boolean;
	onChange: (patch: Partial<InkoopProposalLine>) => void;
	onRemove: (() => void) | null;
}) {
	const ledger = line.ledger_account
		? ledgers.find((l) => l.code === line.ledger_account)
		: null;
	const ledgerDescription = ledger?.description ?? null;
	return (
		<div
			className={`grid grid-cols-12 items-start gap-2 rounded-md border p-2 ${
				line.is_shipping ? 'border-amber-300 bg-amber-50' : ''
			}`}
		>
			<div className={entityIsVatExempt ? 'col-span-5' : 'col-span-3'}>
				<Input
					placeholder="Omschrijving"
					value={line.description}
					onChange={(e) => onChange({ description: e.target.value })}
					disabled={disabled}
					className="h-8"
				/>
				<div className="mt-1 h-4 text-[10px] uppercase leading-4 tracking-wide text-amber-700">
					{line.is_shipping ? 'verzendkosten' : ''}
				</div>
			</div>
			<div className={entityIsVatExempt ? 'col-span-3' : 'col-span-2'}>
				<Input
					list="ledger-options"
					value={line.ledger_account ?? ''}
					onChange={(e) => onChange({ ledger_account: e.target.value || null })}
					placeholder="grootboek"
					disabled={disabled}
					className="h-8 font-mono"
				/>
				<datalist id="ledger-options">
					{ledgers.map((l) => (
						<option key={l.exact_id} value={l.code}>
							{l.code} — {l.description}
						</option>
					))}
				</datalist>
				<div className="mt-1 h-4 truncate text-[10px] leading-4 text-muted-foreground" title={ledgerDescription ?? ''}>
					{ledgerDescription ?? ''}
				</div>
			</div>
			{!entityIsVatExempt && (
				<div className="col-span-2">
					<Select
						value={line.vat_code ?? ''}
						onChange={(e) => {
							const code = e.target.value || null;
							const vat = vatCodes.find((v) => v.code === code);
							const nextPercent = vat?.percentage
								? formatVatPercent(vat.percentage)
								: line.vat_percent;
							onChange({
								vat_code: code,
								vat_percent: nextPercent,
								vat_amount: recalcVatAmount(line.amount_excl_vat, nextPercent),
							});
						}}
						disabled={disabled}
						className="h-8"
					>
						<option value="">—</option>
						{vatCodes.map((v) => (
							<option key={v.exact_id} value={v.code}>
								{v.code} — {v.description}
								{v.percentage ? ` (${formatVatPercent(v.percentage)}%)` : ''}
							</option>
						))}
					</Select>
					<div className="mt-1 h-4" />
				</div>
			)}
			<div className="col-span-2">
				<AmountInput
					value={line.amount_excl_vat}
					onChange={(v) =>
						onChange(
							entityIsVatExempt
								? { amount_excl_vat: v, vat_amount: '0.00' }
								: { amount_excl_vat: v, vat_amount: recalcVatAmount(v, line.vat_percent) },
						)
					}
					disabled={disabled}
					sign={isCredit ? -1 : 1}
				/>
				<div className="mt-1 h-4 text-[10px] leading-4 text-muted-foreground">
					{(() => {
						const excl = line.amount_excl_vat ? Number(line.amount_excl_vat) : 0;
						if (entityIsVatExempt) {
							return excl ? `Totaal: ${(isCredit ? -excl : excl).toFixed(2)}` : '';
						}
						const vat = line.vat_amount ? Number(line.vat_amount) : 0;
						const vatRow = line.vat_code ? vatCodes.find((v) => v.code === line.vat_code) : null;
						const reverse = Boolean(vatRow?.is_reverse_charged);
						// Bij verlegd: total = excl (BTW is fictief, telt niet mee).
						const total = reverse ? excl : excl + vat;
						return total ? `Totaal: ${(isCredit ? -total : total).toFixed(2)}` : '';
					})()}
				</div>
			</div>
			{!entityIsVatExempt && (
				<div className="col-span-2">
					<AmountInput
						value={line.vat_amount}
						onChange={(v) => onChange({ vat_amount: v })}
						disabled={disabled}
						sign={isCredit ? -1 : 1}
					/>
					<div className="mt-1 h-4" />
				</div>
			)}
			<div className="col-span-1 flex h-8 items-center justify-end gap-1">
				<button
					type="button"
					title="Verzendkosten markeren"
					onClick={() => onChange({ is_shipping: !line.is_shipping })}
					disabled={disabled}
					className={
						line.is_shipping
							? 'rounded p-1 text-amber-700 hover:bg-amber-100'
							: 'rounded p-1 text-muted-foreground hover:bg-muted'
					}
				>
					🚚
				</button>
				{onRemove && !disabled && (
					<button
						type="button"
						onClick={onRemove}
						className="rounded p-1 text-destructive hover:bg-destructive/10"
						title="Regel verwijderen"
					>
						<Trash2 className="h-3.5 w-3.5" />
					</button>
				)}
			</div>
		</div>
	);
}

// ---------------- Amount input met calculator ----------------

function AmountInput({
	value,
	onChange,
	disabled,
	sign,
}: {
	value: string | null;
	onChange: (v: string | null) => void;
	disabled: boolean;
	sign: 1 | -1;
}) {
	const [editing, setEditing] = useState(false);
	const [raw, setRaw] = useState(value ?? '');
	useEffect(() => {
		setRaw(value ?? '');
	}, [value]);

	const commit = () => {
		setEditing(false);
		const trimmed = raw.trim();
		if (!trimmed) {
			onChange(null);
			return;
		}
		// Calculator-modus: ondersteun simpele expressies (+ - * /).
		if (/[+\-*/]/.test(trimmed) && /^[\d\s+\-*/.]+$/.test(trimmed)) {
			try {
				// eslint-disable-next-line no-new-func
				const result = Function(`"use strict"; return (${trimmed})`)();
				if (typeof result === 'number' && Number.isFinite(result)) {
					const fixed = result.toFixed(2);
					setRaw(fixed);
					onChange(fixed);
					return;
				}
			} catch {
				// laat raw staan, gebruiker fixt
			}
		}
		const parsed = Number(trimmed.replace(',', '.'));
		if (Number.isFinite(parsed)) {
			const fixed = parsed.toFixed(2);
			setRaw(fixed);
			onChange(fixed);
		}
	};

	const display = editing
		? raw
		: value
		? sign === -1
			? `−${value}`
			: value
		: '';

	return (
		<div className="relative">
			<Input
				value={display}
				placeholder="0,00"
				onFocus={() => setEditing(true)}
				onChange={(e) => setRaw(e.target.value)}
				onBlur={commit}
				onKeyDown={(e) => {
					if (e.key === 'Enter') {
						e.preventDefault();
						commit();
					}
					if (e.key === 'Escape') {
						setRaw(value ?? '');
						setEditing(false);
					}
				}}
				disabled={disabled}
				className={`h-8 pl-6 text-right font-mono ${sign === -1 && value ? 'text-amber-700' : ''}`}
				inputMode="decimal"
			/>
			<span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
				<Calculator className="h-3 w-3" />
			</span>
		</div>
	);
}

// ---------------- Comment ----------------

function CommentCard({
	draft,
	setDraft,
	disabled,
}: {
	draft: InkoopProposal;
	setDraft: React.Dispatch<React.SetStateAction<InkoopProposal | null>>;
	disabled: boolean;
}) {
	return (
		<Card>
			<CardHeader className="pb-3">
				<CardTitle className="text-sm font-medium">Toelichting</CardTitle>
			</CardHeader>
			<CardContent className="text-sm">
				<Textarea
					rows={2}
					placeholder="Optionele toelichting (niet zichtbaar in Exact)"
					value={draft.header.comment}
					onChange={(e) =>
						setDraft((d) => (d ? { ...d, header: { ...d.header, comment: e.target.value } } : d))
					}
					disabled={disabled}
					maxLength={2000}
				/>
			</CardContent>
		</Card>
	);
}

// ---------------- Create-creditor dialog ----------------

interface CreatePrefill {
	name: string;
	address_line1: string;
	postcode: string;
	city: string;
	chamber_of_commerce: string;
	vat_number: string;
	email: string;
	phone: string;
}

function CreateCreditorDialog({
	entityCode,
	prefill,
	onClose,
	onCreated,
}: {
	entityCode: string;
	prefill: CreatePrefill;
	onClose: () => void;
	onCreated: (c: { exact_id: string; name: string; code: string | null }) => void;
}) {
	const [form, setForm] = useState(prefill);
	const [error, setError] = useState<string | null>(null);
	const mutation = useMutation({
		mutationFn: () =>
			createExactCreditor(entityCode, {
				name: form.name.trim(),
				address_line1: form.address_line1 || null,
				postcode: form.postcode || null,
				city: form.city || null,
				chamber_of_commerce: form.chamber_of_commerce || null,
				vat_number: form.vat_number || null,
				email: form.email || null,
				phone: form.phone || null,
			}),
		onSuccess: (res) => onCreated(res),
		onError: (err: unknown) => {
			const body = err && typeof err === 'object' && 'body' in err ? (err as { body: unknown }).body : err;
			setError(typeof body === 'object' ? JSON.stringify(body) : String(body));
		},
	});

	return (
		<div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 p-4">
			<Card className="w-full max-w-2xl">
				<CardHeader className="flex flex-row items-center justify-between pb-3">
					<CardTitle className="text-sm font-medium">Nieuwe crediteur in Exact</CardTitle>
					<Button variant="ghost" size="sm" onClick={onClose}>
						<X className="h-4 w-4" />
					</Button>
				</CardHeader>
				<CardContent className="space-y-3 text-sm">
					<p className="text-xs text-muted-foreground">
						Velden zijn voor-ingevuld vanuit het document. Pas aan en bevestig om de relatie aan te maken in Exact.
					</p>
					<div>
						<Label>Naam *</Label>
						<Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
					</div>
					<div className="grid grid-cols-2 gap-3">
						<div>
							<Label>Adres</Label>
							<Input value={form.address_line1} onChange={(e) => setForm({ ...form, address_line1: e.target.value })} />
						</div>
						<div className="grid grid-cols-2 gap-2">
							<div>
								<Label>Postcode</Label>
								<Input value={form.postcode} onChange={(e) => setForm({ ...form, postcode: e.target.value })} />
							</div>
							<div>
								<Label>Plaats</Label>
								<Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
							</div>
						</div>
					</div>
					<div className="grid grid-cols-2 gap-3">
						<div>
							<Label>KvK</Label>
							<Input value={form.chamber_of_commerce} onChange={(e) => setForm({ ...form, chamber_of_commerce: e.target.value })} />
						</div>
						<div>
							<Label>BTW</Label>
							<Input value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} />
						</div>
					</div>
					<div className="grid grid-cols-2 gap-3">
						<div>
							<Label>E-mail</Label>
							<Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
						</div>
						<div>
							<Label>Telefoon</Label>
							<Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
						</div>
					</div>
					{error && <p className="text-xs text-destructive">{error}</p>}
					<div className="flex justify-end gap-2 pt-2">
						<Button variant="outline" size="sm" onClick={onClose}>
							Annuleren
						</Button>
						<Button size="sm" onClick={() => mutation.mutate()} disabled={mutation.isPending || !form.name.trim()}>
							<ExternalLink className="mr-1.5 h-4 w-4" />
							{mutation.isPending ? 'Aanmaken in Exact…' : 'Aanmaken in Exact'}
						</Button>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}

// ---------------- Crediteur-defaults dialog ----------------
//
// Per crediteur: default-grootboek, verzendkosten-grootboek, regelboeking-modus
// en de betalingsreferentie-template. Placeholders worden uitgelegd onder het
// veld + live preview op basis van de huidige factuur.

import { putCreditorDefaults } from '@/lib/mutations';

function CreditorDefaultsDialog({
	entityCode,
	creditor,
	onClose,
	onSaved,
}: {
	entityCode: string;
	creditor: ExactCreditor;
	onClose: () => void;
	onSaved: () => void;
}) {
	const queryClient = useQueryClient();
	const defaultsQuery = useQuery({
		queryKey: ['creditor-defaults', entityCode, creditor.exact_id],
		queryFn: () => api.exactCreditorDefaults(entityCode, creditor.exact_id),
	});
	const ledgersQuery = useQuery({
		queryKey: ['exact-ledgers', entityCode],
		queryFn: () => api.exactLedgers(entityCode),
	});
	const vatCodesQuery = useQuery({
		queryKey: ['exact-vat-codes', entityCode],
		queryFn: () => api.exactVatCodes(entityCode),
	});

	const [defaultLedger, setDefaultLedger] = useState('');
	const [shippingLedger, setShippingLedger] = useState('');
	const [defaultBtwCode, setDefaultBtwCode] = useState('');
	const [splitMode, setSplitMode] = useState<'single' | 'multi'>('multi');
	const [paymentTemplate, setPaymentTemplate] = useState('');
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		const d = defaultsQuery.data?.defaults;
		setDefaultLedger(d?.default_ledger_account ?? '');
		setShippingLedger(d?.shipping_ledger_account ?? '');
		setDefaultBtwCode(d?.default_btw_code ?? '');
		setSplitMode(d?.line_split_mode ?? 'multi');
		setPaymentTemplate(d?.payment_reference_template ?? '');
	}, [defaultsQuery.data]);

	const saveMutation = useMutation({
		mutationFn: () =>
			putCreditorDefaults(entityCode, creditor.exact_id, {
				default_ledger_account: defaultLedger.trim() || null,
				shipping_ledger_account: shippingLedger.trim() || null,
				default_btw_code: defaultBtwCode.trim() || null,
				line_split_mode: splitMode,
				payment_reference_template: paymentTemplate.trim() || null,
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['creditor-defaults', entityCode, creditor.exact_id] });
			queryClient.invalidateQueries({ queryKey: ['inkoopfactuur'] });
			onSaved();
		},
		onError: (err: unknown) => {
			const body = err && typeof err === 'object' && 'body' in err ? (err as { body: unknown }).body : err;
			setError(typeof body === 'object' ? JSON.stringify(body) : String(body));
		},
	});

	// Live preview van de template met dummy-context van deze crediteur.
	const today = new Date().toISOString().slice(0, 10);
	const monthNames = [
		'januari', 'februari', 'maart', 'april', 'mei', 'juni',
		'juli', 'augustus', 'september', 'oktober', 'november', 'december',
	];
	const month = monthNames[Number.parseInt(today.slice(5, 7), 10) - 1] + ' ' + today.slice(0, 4);
	const periode = today.slice(0, 7).replace('-', '/');
	const previewSrc = paymentTemplate
		.split('[klantnr]').join(creditor.our_account ?? '?klantnr')
		.split('[factuurnummer]').join('?factuurnummer')
		.split('[ordernummer]').join('?ordernummer')
		.split('[naam_crediteur]').join(creditor.name)
		.split('[maand]').join(month)
		.split('[periode]').join(periode);

	return (
		<div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 p-4">
			<Card className="w-full max-w-xl">
				<CardHeader className="flex flex-row items-center justify-between pb-3">
					<CardTitle className="text-sm font-medium">Crediteur-instellingen — {creditor.name}</CardTitle>
					<Button variant="ghost" size="sm" onClick={onClose}>
						<X className="h-4 w-4" />
					</Button>
				</CardHeader>
				<CardContent className="space-y-3 text-sm">
					<div className="grid grid-cols-2 gap-3">
						<div>
							<Label>Standaard grootboek</Label>
							<Select value={defaultLedger} onChange={(e) => setDefaultLedger(e.target.value)}>
								<option value="">— niet ingesteld —</option>
								{(ledgersQuery.data?.items ?? []).map((l) => (
									<option key={l.exact_id} value={l.code}>
										{l.code} — {l.description}
									</option>
								))}
							</Select>
						</div>
						<div>
							<Label>Verzendkosten-grootboek</Label>
							<Select value={shippingLedger} onChange={(e) => setShippingLedger(e.target.value)}>
								<option value="">— niet ingesteld —</option>
								{(ledgersQuery.data?.items ?? []).map((l) => (
									<option key={l.exact_id} value={l.code}>
										{l.code} — {l.description}
									</option>
								))}
							</Select>
						</div>
					</div>
					<div className="grid grid-cols-2 gap-3">
						<div>
							<Label>Standaard BTW-code</Label>
							<Select value={defaultBtwCode} onChange={(e) => setDefaultBtwCode(e.target.value)}>
								<option value="">— niet ingesteld —</option>
								{(vatCodesQuery.data?.items ?? []).map((v) => (
									<option key={v.exact_id} value={v.code}>
										{v.code} — {v.description}
									</option>
								))}
							</Select>
						</div>
						<div>
							<Label>Regelboeking</Label>
							<Select value={splitMode} onChange={(e) => setSplitMode(e.target.value === 'single' ? 'single' : 'multi')}>
								<option value="multi">Multi-regel</option>
								<option value="single">Single-regel (samenvoegen)</option>
							</Select>
						</div>
					</div>
					<div>
						<Label>Betalingsreferentie-template</Label>
						<Input
							value={paymentTemplate}
							onChange={(e) => setPaymentTemplate(e.target.value)}
							placeholder="Klantnummer: [klantnr] / Factuurnummer: [factuurnummer]"
						/>
						<p className="mt-1 text-[11px] text-muted-foreground">
							Placeholders: <code>[klantnr]</code>, <code>[factuurnummer]</code>,{' '}
							<code>[ordernummer]</code>, <code>[naam_crediteur]</code>, <code>[maand]</code>,{' '}
							<code>[periode]</code>. Leeglaten = factuurnummer.
						</p>
						{paymentTemplate && (
							<p className="mt-1 rounded bg-muted/40 px-2 py-1 text-[11px] font-mono">
								Voorbeeld: {previewSrc}
							</p>
						)}
					</div>
					{error && <p className="text-xs text-destructive">{error}</p>}
					<div className="flex justify-end gap-2 pt-2">
						<Button variant="outline" size="sm" onClick={onClose}>
							Annuleren
						</Button>
						<Button size="sm" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
							{saveMutation.isPending ? 'Opslaan…' : 'Opslaan'}
						</Button>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}

function ExactBookedBanner({
	entryId,
	alreadyPresent,
	paymentStatus,
	paymentDate,
	paymentSyncedAt,
}: {
	entryId: string;
	alreadyPresent: boolean;
	paymentStatus: 'paid' | 'partial' | 'open' | null;
	paymentDate: string | null;
	paymentSyncedAt: string | null;
}) {
	const [copied, setCopied] = useState(false);
	const onCopy = async () => {
		try {
			await navigator.clipboard.writeText(entryId);
			setCopied(true);
			setTimeout(() => setCopied(false), 1500);
		} catch {
			// no-op
		}
	};
	return (
		<div className="flex flex-wrap items-center gap-3 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900">
			<CheckCircle2 className="h-4 w-4" />
			<span>
				{alreadyPresent
					? 'Deze factuur stond al in Exact (idempotent overgeslagen).'
					: 'Geboekt in Exact als concept-inkoopboeking.'}
			</span>
			{paymentStatus && (
				<span
					className="inline-flex items-center gap-1.5"
					title={paymentSyncedAt ? `Bijgewerkt: ${paymentSyncedAt}` : undefined}
				>
					<span className={`h-2 w-2 rounded-full ${PAYMENT_STATUS_DOT[paymentStatus]}`} aria-hidden />
					<span>
						{PAYMENT_STATUS_LABELS[paymentStatus]}
						{paymentStatus === 'paid' && paymentDate && ` op ${formatDateISO(paymentDate)}`}
					</span>
				</span>
			)}
			<span className="ml-auto inline-flex items-center gap-1.5 font-mono text-[11px]">
				<span className="text-emerald-700">EntryID</span>
				<span>{entryId}</span>
				<button
					type="button"
					onClick={onCopy}
					className="rounded p-1 text-emerald-700 hover:bg-emerald-100"
					title="Kopieer EntryID"
				>
					<Copy className="h-3 w-3" />
				</button>
				{copied && <span className="text-emerald-700">gekopieerd</span>}
			</span>
		</div>
	);
}
